/* eslint-disable @typescript-eslint/prefer-for-of */
/* eslint-disable @tencent/i18next/no-literal-string */
/**
 * 离线编辑数据处理逻辑
 */
import { IMutationsPackExtended, RecoveryStatus } from '@tencent/slide-sdk-persistent';
//
import { type ILogger } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { OnlineEditServices } from '@src/slide/domain/slide-app/scenes/online/core/online-edit-services.core';
import { IOfflineEditContext } from '../core/interfaces/offline-edit-context.interace';
import { IOfflineDBService } from '../core/interfaces/offline-db-service.interface';
import { IOfflineMutationsService } from '../core/interfaces/offline-mutations-service.interface';
import { PadIdType } from '../models/padInfo';
import { mutationsPack2OfflineData } from '../models/offlineData';
import { createCurClientVars } from '../models/clientVars';
import { OfflineEditReport } from '../constants';
import { waitForSlideAppReady } from '../utils';
import { IOfflineEditService } from '../core/interfaces/offline-edit-service.interface';
import { IEventManager } from '@tencent/slide-sdk-model';
import { CACHE_UPDATE_TITLE } from '@src/slide/domain/slide-app/scenes/online/managers/slide-title-manager.online';
import { SlideDataServices } from '../../../core/slide-data-services.core';
import type { SlideFileInstance } from '../../../core/managers/slide-file-manager';

const MODULE_NAME = '[OMS]';

export class OfflineMutationsService implements IOfflineMutationsService {
    private mutationsPacks: IMutationsPackExtended[];
    private slideFileInstance?: SlideFileInstance | Promise<SlideFileInstance>;

    public constructor(
        private context: IOfflineEditContext,
        private DBService: IOfflineDBService,
        private logger: ILogger,
        private sdkEvent: IEventManager,
        private offlineEditService: IOfflineEditService,
    ) {
        this.mutationsPacks = [];
        this.init();
    }

    public getMutationsPacks = () => this.mutationsPacks;

    /**
     * 应用离线数据 ( for sdk )
     * @param offlineMutationsPack 离线数据 mutations
     * @param isOnline 网络状况
     */
    public applyOfflineData = async (
        offlineMutationsPack: IMutationsPackExtended[],
        recoveryCallback: () => Promise<void>,
    ) => {
        this.logger.tdw?.({
            opername: 'quality_slide',
            module: 'offline-edit',
            action: 'offline-edit-error-reason',
            ver5: OfflineEditReport.LAUNCHED,
        });
        const collaborationManager = await OnlineEditServices.CollaborationManager.promise();
        const persistent = collaborationManager.getPersistent();
        if (!offlineMutationsPack?.length) {
            this.logger.info(MODULE_NAME, 'offlineMutationsPack is empty');
            await recoveryCallback();
            return;
        }
        offlineMutationsPack.forEach((item) => {
            const itemRef: IMutationsPackExtended = item;
            this.mutationsPacks.push(item);
            itemRef.sendedCount += 1;
        });
        this.logger.info(MODULE_NAME, `all mutationsPacks length: ${this.mutationsPacks.length}`);
        // this.logger.debug(MODULE_NAME, `all mutationsPacks: ${this.mutationsPacks}`);
        if (this.context.isOnline()) {
            // 在线打开 - 服务端数据可能不包含离线数据 需提交离线数据并应用
            this.logger.info(MODULE_NAME, 'isOnline, ready to recovery');
            persistent.setMutationsPacks([...this.mutationsPacks]);
            // persistent.startToRecovery();
            await recoveryCallback();
            return;
        }
        // 离线打开 - 快照已包含离线数据仅需提交离线数据
        this.logger.info(MODULE_NAME, 'isOffline, ready to send');
        persistent.didRecoverPersistentData();
        collaborationManager.addNativeOfflineDataToQueue([...this.mutationsPacks]);
    };

    private updateAndSaveCurClientVars = async () => {
        const presentationData = process.env.WORKER_DISABLE ? null : await this.getPresentationData();
        const clientVars = await createCurClientVars(presentationData);
        this.offlineEditService.syncClientVarsToMemory(clientVars);
        return this.offlineEditService.syncCurClientVarsToDB();
    };

    private async getPresentationData() {
        const instance = await this.getSlideFileInstance();
        const presentationStoreService = await SlideDataServices.SDKDataServices.PresentationStoreService.promise();
        const { data } = await instance.slideFile
            .fromPresentationData(presentationStoreService.getPresentation().getWritableOwnedData());
        return data;
    }

    private async getSlideFileInstance(): Promise<SlideFileInstance> {
        if (!this.slideFileInstance) {
            this.slideFileInstance = SlideDataServices.SlideFileManager()!.createInstance();
        }
        if (this.slideFileInstance instanceof Promise) {
            this.slideFileInstance = await this.slideFileInstance;
        }
        return this.slideFileInstance;
    }

    private async init() {
        await waitForSlideAppReady();
        this.logger.info(MODULE_NAME, '[init] onSlideAppReady');
        this.registerSlideAppReadyListener();
        this.registerMutationsListener();
    }

    private registerSlideAppReadyListener() {
        const { secretId, globalPadId } = this.context;
        this.logger.info(MODULE_NAME, `registerOfflineEditHandler -
        onSlideAppReady -
        secretId:${secretId} -
        globalPadId:${globalPadId}`);
        window.addEventListener('beforeunload', () => {
            this.offlineEditService.onBeforeUnloadCb();
        });
    }

    /**
     * 文档变更事件监听
     * 更新快照场景：
     * 1. onCommitCommand 文档数据发生变更(离线编辑数据未提交成功 & 在线编辑数据提交成功 & 在线协同数据)
     * 2. CACHE_UPDATE_TITLE 离线更新标题需将离线标题同步至数据库
     * 更新离线数据场景：
     * 1. onReplaceAllData 文档变更数据提交成功
     * 2. onAddOrUpdateData 文档数据变更(在线提交成功后续会触发 onReplaceAllData，离线未提交成功则无法触发)
     * 3. onRemoveData 删除本地离线数据
     */
    private registerMutationsListener() {
        this.logger.info(MODULE_NAME, 'registerMutationsListener');
        window.addEventListener(CACHE_UPDATE_TITLE, (e) => {
            this.updatePadInfoFiled({
                title: (e as any).detail?.title || '空白幻灯片',
            });
            this.updateAndSaveCurClientVars();
        });
        this.sdkEvent.mutationEvents.onCommitCommand(this.updateAndSaveCurClientVars);
        this.sdkEvent.revisionEvents.onUpdateBaseRevision(this.updateAndSaveCurClientVars);
        this.sdkEvent.persistentEvents.onReplaceAllData(this.onReplaceAllData);
        this.sdkEvent.persistentEvents.onAddOrUpdateData(this.additionOrUpdate);
        this.sdkEvent.persistentEvents.onRemoveData(this.deletionMultiple);
        this.sdkEvent.persistentEvents.onRecoveryStatusChange((recoveryStatus) => {
            if (recoveryStatus === RecoveryStatus.DidRecoverData) {
                this.restored();
            }
        });
    }

    /**
     * 立即存储内存中的数据到数据库
     * source 保存逻辑调用方用来日志区分
     */
    private async saveCacheImmediately(source: string) {
        this.logger.info(MODULE_NAME, `ready to saveCacheImmediately
        source: ${source}
        secretId: ${this.context.secretId},
        globalPadId: ${this.context.globalPadId},
        mutationsPackCachesLength: ${this.mutationsPacks.length}`);
        // this.logger.debug(MODULE_NAME, this.mutationsPacks);
        const res = this.mutationsPacks.length
            ? await this.saveCacheImmediatelyAndUpdateDB(source)
            : await this.saveCacheImmediatelyAndRemoveDB(source);
        if (!res) {
            this.offlineEditService.handleDatabaseError('saveCacheImmediately', 'failed');
        }
    }

    /**
     * 更新DB中的离线数据
     * @param source
     */
    private async saveCacheImmediatelyAndUpdateDB(source: string) {
        try {
            // 更新离线数据
            const dataItem = mutationsPack2OfflineData(this.context, this.mutationsPacks);
            const result = await this.DBService.updateOfflineData(dataItem);
            if (result.error) {
                throw result.error;
            }
            this.logger.info(MODULE_NAME, `saveCacheImmediatelyAndUpdateDB ${source} success`);
            // this.logger.debug(MODULE_NAME, result.data);
            return true;
        } catch (err) {
            this.logger.error(MODULE_NAME, `saveCacheImmediatelyAndUpdateDB ${source}`, err?.toString());
            return false;
        }
    }

    /**
     * 删除DB中的离线数据
     * @param source
     */
    private async saveCacheImmediatelyAndRemoveDB(source: string) {
        try {
            const result = await this.DBService.updateOfflineData();
            if (result.error) {
                throw result.error;
            }
            this.logger.info(MODULE_NAME, `saveCacheImmediatelyAndRemoveDB ${source} success`);
            // this.logger.debug(MODULE_NAME, result.data);
            return true;
        } catch (err) {
            this.logger.error(MODULE_NAME, `saveCacheImmediatelyAndRemoveDB ${source}`, err?.toString());
            return false;
        }
    }

    /**
     * 有 mutationsPack 提交成功
     * 更新本地离线数据
     */
    private onReplaceAllData = (mutationsPacks: IMutationsPackExtended[]) => {
        this.logger.info(MODULE_NAME, 'replaceAllData length:', mutationsPacks.length);
        // this.logger.debug(MODULE_NAME, 'replaceAllData is ', mutationsPacks);
        // 替换本地离线数据
        this.mutationsPacks = [...mutationsPacks];
        this.firstUpdateSyncPadInfo();
        return this.saveCacheImmediately('onReplaceAllData');
    };

    /**
     * 业务 - 添加或更新 mutationsPack
     */
    private additionOrUpdate = (mutationsPacks: IMutationsPackExtended[]) => {
        mutationsPacks.forEach(this.handleAdditionOrUpdate);
        return this.saveCacheImmediately('additionOrUpdate');
    };

    /**
     * 业务 - 删除多个目标
     */
    private deletionMultiple = (taskIds: number[]) => {
        this.handleDeletion(taskIds);
        return this.saveCacheImmediately('deletionMultiple');
    };

    /**
     * 业务 - 恢复完成
     * @param arg
     */
    private restored = async () => {
        this.logger.info(MODULE_NAME, '[restored] invoke');
        // 恢复完成之后需要更新快照
        this.updateAndSaveCurClientVars();
    };

    /**
     * 添加或更新数据
     * @param targetItem
     * @param items
     */
    private handleAdditionOrUpdate = (modifiedMutationsPack: IMutationsPackExtended) => {
        const mutationsPack = this.mutationsPacks.find(item => item.taskId === modifiedMutationsPack.taskId);
        if (mutationsPack) {
            Reflect.set(mutationsPack, 'mutations', modifiedMutationsPack.mutations);
            this.logger.info(MODULE_NAME, 'handleAdditionOrUpdate update mutationsPack, taskId:', mutationsPack.taskId);
            return;
        }

        this.mutationsPacks.push(modifiedMutationsPack);
        this.logger.info(
            MODULE_NAME, 'handleAdditionOrUpdate add mutationsPack, taskId:',
            modifiedMutationsPack.taskId,
        );
    };

    /**
     * 处理删除业务
     * @param targetTaskId
     * @param items
     */
    private handleDeletion = (taskIds: number[]) => {
        taskIds.forEach((taskId) => {
            const index = this.mutationsPacks.findIndex(({ taskId: taskIdFromMutationsPack, revision }) => {
                if (taskId === taskIdFromMutationsPack) {
                    this.logger.info(MODULE_NAME, 'handleDeletion', {
                        revision,
                        taskId: taskIdFromMutationsPack,
                    });
                    return true;
                }
                return false;
            });
            index !== -1 && this.mutationsPacks.splice(index, 1);
        });
    };

    /**
     * 文档首次被编辑后更新本地数据库 padInfo 中 isEdited 为 true
     */
    private async firstUpdateSyncPadInfo() {
        const visible = await SlideDataServices.DocInfoManager.promise().then(service => service.visible);
        if (visible) {
            return;
        }
        // 文档编辑触发 onReplaceAllData, visible = false 文档未被编辑过 即首次编辑
        this.logger.info(MODULE_NAME, 'firstUpdateSyncPadInfo');
        await this.updatePadInfoFiled({
            isEdited: true,
        });
    }

    /**
     * 修改本地数据库 padInfo 中部分字段
     * @param data
     */
    private async updatePadInfoFiled(data) {
        const curPadInfo = await this.DBService.getPadInfo(this.context.secretId, PadIdType.SECRET);
        this.logger.info(MODULE_NAME, 'updatePadInfoFiled data: ', data, curPadInfo);
        if (!curPadInfo) {
            return;
        }
        const newPadInfo = Object.assign(curPadInfo, data);
        this.DBService.updatePadInfo({
            isAlreadyExist: true,
            padInfo: newPadInfo,
        });
    }
}
